<template>
  <div class="employee-to-prepare-position">
    <v-dialog
      v-model="value"
      scrollable
      max-width="560"
      @click:outside="resetSelection"
      style="position: relative; overflow-y: unset"
    >
      <v-card class="wizard-popup px-4 py-4">
        <div class="d-flex flex-column mt-2">
          <div class="d-flex justify-space-between mb-5">
            <div>
              <AvatarWithAvailability
                :first-name="currentEmployee && currentEmployee.first_name"
                :last-name="currentEmployee && currentEmployee.last_name"
                :isAllocated="false"
                :photoUrl="currentEmployee && currentEmployee.avatar"
              ></AvatarWithAvailability>
            </div>
            <v-btn icon @click="resetSelection" color="#98A2B3" :disabled="isAssigning">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <div class="modal-title mb-1">Assign to prepare for position</div>
          <div class="modal-description mb-4">
            {{
              `Pick a position for ${(currentEmployee && currentEmployee.first_name) || ""} ${
                (currentEmployee && currentEmployee.last_name) || ""
              } to prepare for. Any active position
          can be selected, except the one primary occupied by ${
            (currentEmployee && currentEmployee.first_name) || ""
          } ${(currentEmployee && currentEmployee.last_name) || ""}.`
            }}
          </div>
        </div>
        <div class="prepare-position-picker mb-2" :class="{ 'disabled-content': isAssigning }">
          <ExPositionPicker
            :items="processedItems"
            @item-selected="handleItemSelected"
            :value="selectedItems"
            @clear-selection="clearSelection"
            @load-more="$emit('load-more' )"
            @search="$emit('search', $event)"
            :isLoadingMore="isLoadingMore"
            :hasMoreData="hasMoreData"
            :disabled="isAssigning"
          />
        </div>
        <div class="effective-wizard" :class="{ 'disabled-content': isAssigning }">
          <div class="mb-2 effective-class ml-4 mt-2">
            <div class="effective-title">Effective Date</div>
            <div class="effective-date"><DatePicker :value="formattedDate" @onApplyDate="handleApplyDate" :modal="true" :isDisableDatepicker="isAssigning"/></div>
          </div>
          <div class="mb-7 ml-2" v-if="currentEmployee && currentEmployee.user && currentEmployee.access && currentEmployee.access.is_suspended === false">
            <CheckboxInput
              class="effective-checkbox"
              v-model="isNotify"
              label="Notify Employee"
              :disabled="isAssigning"
            />
            <span class="effective-checkbox-description"
              >The employee will be notified via email</span
            >
          </div>
          <div class="notification-warning mb-7 ml-3" v-else>
            <v-img
              class="notify-icon"
              :src="require('@components/assets/notify-outer.svg')"
              contain
              width="30"
              height="30"
            />
            <div class="text-content">
              <span class="main-text">Employee cannot be notified</span>
              <span class="sub-text">because it does not have access</span>
            </div>
          </div>
        </div>

        <Button
          style="height: 40px; border-radius: 8px"
          :normal="(positionSelected || isAssigning) ? 'normal-disable' : 'normal-hover'"
          :label="'Assign'"
          @onClick="onAssign"
          :loading="(isAssigning || $loader.isAppLoading) ? 'left' : null"
          :fullwidth="'default'"
        ></Button>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, watch } from "vue";
import "./positionPicker.css";
import AvatarWithAvailability from "@components/organizations/employees/AvatarWithAvailability.vue";
import ExPositionPicker from "@components/PreparePositionPicker/PositionPicker.vue";
import Button from "@components/buttons/Button.vue";
import DatePicker from "@components/DatePicker/DatePicker.vue";
import CheckboxInput from "@components/Checkbox/CheckboxInput.vue";
const positionSelected = ref(true);
const selectedItems = ref(null);
const effective_date = ref(new Date());
const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  currentEmployee: {
    type: Object,
    default: null,
  },
  label: {
    type: String,
    required: true,
  },
  photoUrl: String,
  items: {
    type: Array,
    default: () => [],
  },
  isLoadingMore: {
    type: Boolean,
    default: false,
  },
  hasMoreData: {
    type: Boolean,
    default: true,
  },
  isAssigning: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["input", "onAssignPreparePosition"]);
const formattedDate = computed(() =>
  effective_date.value.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
);
const isNotify = ref(true);
const processedItems = computed(() => {
  return props.items.map((item) => {
    let occupied_by = Array.isArray(item.occupied_by) ? item.occupied_by : item.occupied_by ? [item.occupied_by] : [];
    
    // Process occupied_by to split full_name into firstName and lastName
    occupied_by = occupied_by.map((emp) => {
      const nameParts = emp.full_name ? emp.full_name.split(' ') : [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      return {
        img: emp.avatar || "",
        firstName: firstName,
        lastName: lastName,
        fullName: emp.full_name || "",
      };
    });
    
    return {
      ...item,
      occupied_by,
    };
  });
});

const handleItemSelected = (selectedItem) => {
  // Prevent selection during assignment process
  if (props.isAssigning) return;
  
  if (selectedItem) {
    positionSelected.value = false;
    selectedItems.value = selectedItem;
  } else {
    positionSelected.value = true;
    selectedItems.value = null;
  }
};
const clearSelection = () => {
  positionSelected.value = true;
  selectedItems.value = null;
  emit("search", "");
};
const handleApplyDate = (date) => {
  // Prevent date changes during assignment
  if (props.isAssigning) return;
  
  if (date) {
    effective_date.value = new Date(date); // ✅ Set new date
  }
};
const resetSelection = () => {
  // Prevent closing during assignment
  if (props.isAssigning) return;
  
  positionSelected.value = true;
  selectedItems.value = null;
  effective_date.value = new Date();
  emit("input", false);
};
const onAssign = () => {
  // Prevent multiple clicks during assignment
  if (props.isAssigning) return;
  
  const isoToday = formatLocalDateToISO(effective_date.value);
  const tempVariable = JSON.parse(JSON.stringify(selectedItems.value));
  positionSelected.value = true;
  selectedItems.value = null;
  const onlyDate = isoToday;
  if(props.currentEmployee && props.currentEmployee.access && props.currentEmployee.access.is_suspended == false){
    isNotify.value = true;
  }
  else {
    isNotify.value = false;
  }
  const obj = {
    position: tempVariable,
    effective_date: onlyDate,
    isNotify: isNotify.value,
  };
  emit("onAssignPreparePosition", obj);
  selectedItems.value = null;
};
const formatLocalDateToISO = (dateStr) => {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};
watch(
  () => props.value,
  (newVal) => {
    if (newVal) {
      effective_date.value = new Date(); // Set to today's date when dialog opens
    }
  }
);
</script>

<style scoped>
.wizard-popup {
  background-image: url("@components/assets/backgrounds/background_assign_position_pattern.svg");
  background-size: 480px 331px;
  background-position: -90% 10%;
}
.modal-title {
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: 0em;
  color: #101828;
}
.modal-description {
  color: #666666;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0em;
}
.modal-content::-webkit-scrollbar-button {
  display: none !important;
}
.modal-content::-webkit-scrollbar {
  width: 8px;
}
.modal-content::-webkit-scrollbar-thumb {
  background-color: transparent;
}
.prepare-position-picker ::v-deep(.custom-response-picker) {
  width: 100% !important;
}
.date-text {
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  color: var(--Headings-or-Titles, #344054);
}
.date-conatiner {
  display: flex;
  align-items: center;
  gap: 12px;
}
::v-deep(.v-dialog) {
  overflow-y: unset !important;
}
.date-conatiner ::v-deep(.date-picker-dropdown) {
  top: -366px !important;
  left: 158px !important;
}
.effective-wizard .effective-class {
  display: flex;
}
.effective-wizard .effective-date {
  margin-top: -10px;
  margin-left: 10px;
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  color: #344054;
}
.effective-wizard .effective-title {
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  color: #344054;
}
::v-deep .effective-wizard .effective-checkbox .icon-check .v-btn {
  width: 24px !important;
}
::v-deep .v-input__slot {
  height: 44px !important;
}
.effective-wizard .effective-checkbox-description {
  color: #475467;
  font-size: 14px;
  position: relative;
  top: -10px;
  left: 28px;
}
.effective-wizard .notification-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.effective-wizard .notify-icon {
  margin-top: 2px;
  position: absolute;
}
.effective-wizard .text-content {
  display: flex;
  flex-direction: column;
  position: relative;
  left: 38px;
  top: 5px;
}
.effective-wizard .main-text {
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  color: #344054;
}
.effective-wizard .sub-text {
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  color: #475467;
}

.disabled-content {
  pointer-events: none;
  opacity: 0.6;
  transition: opacity 0.3s ease;
}
</style>
